import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import { useTeam } from "@/context/TeamContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, CalendarBlank, MapPin, House, Airplane, SoccerBall, CaretRight } from "@phosphor-icons/react";
import { TIME_OPTIONS } from "@/lib/timeOptions";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

const emptyForm = { opponent: "", date: "", time: "09:00", location: "", is_home: true };

export default function Games() {
  const navigate = useNavigate();
  const { currentTeam, currentTeamId, teams, currentRole, loading: teamsLoading } = useTeam();
  const isViewer = currentRole === "viewer";
  const isMember = currentRole === "member";
  const canManage = !isViewer && !isMember;
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!currentTeamId) return;
    try {
      const { data } = await api.get(`/teams/${currentTeamId}/games`);
      setGames(data);
    } finally {
      setLoading(false);
    }
  }, [currentTeamId]);

  useEffect(() => {
    if (teamsLoading) return;
    if (teams.length === 0) {
      navigate("/setup");
      return;
    }
    if (currentTeamId) {
      setLoading(true);
      load();
    }
  }, [teamsLoading, teams.length, currentTeamId, load, navigate]);

  const openAdd = () => {
    setForm(emptyForm);
    setError("");
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = {
        opponent: form.opponent.trim(),
        date: new Date(`${form.date}T${form.time}`).toISOString(),
        location: form.location.trim(),
        is_home: form.is_home,
        notes: "",
      };
      await api.post(`/teams/${currentTeamId}/games`, payload);
      toast.success("Game scheduled");
      setDialogOpen(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const now = new Date().toISOString();
  const inputCls = "w-full bg-[#0A0A0A] border border-zinc-800 text-white px-3 py-2 rounded-sm focus:outline-none focus:border-[#CCFF00] transition-colors duration-150 [color-scheme:dark]";

  const hasResult = (g) => g.status === "completed" && g.score_for !== null && g.score_for !== undefined && g.score_against !== null && g.score_against !== undefined;
  const resultColor = (g) => {
    if (!hasResult(g)) return "text-zinc-500";
    if (g.score_for > g.score_against) return "text-[#32D74B]";
    if (g.score_for < g.score_against) return "text-[#FF3B30]";
    return "text-zinc-300";
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-label text-xs text-[#CCFF00] mb-1">{currentTeam?.name} · {games.length} games</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-white leading-none">Games</h1>
        </div>
        {canManage && (
          <button
            onClick={openAdd}
            data-testid="add-game-btn"
            className="flex items-center gap-2 bg-[#CCFF00] text-black font-label text-xs px-4 py-3 rounded-sm hover:bg-[#B3E600] hover:-translate-y-0.5 transition-all duration-150"
          >
            <Plus size={16} weight="bold" /> Add game
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-32">
          <SoccerBall size={40} className="text-[#CCFF00] animate-spin" weight="fill" />
        </div>
      ) : games.length === 0 ? (
        <div className="bg-[#121212] border border-dashed border-zinc-800 rounded-sm py-20 flex flex-col items-center" data-testid="games-empty">
          <CalendarBlank size={40} className="text-zinc-700 mb-4" />
          <p className="text-zinc-400 mb-1 font-heading text-2xl">No games yet</p>
          <p className="text-zinc-600 text-sm mb-6">{canManage ? "Schedule your first fixture." : "No games scheduled yet."}</p>
          {canManage && (
            <button onClick={openAdd} className="flex items-center gap-2 bg-[#CCFF00] text-black font-label text-xs px-4 py-2 rounded-sm">
              <Plus size={16} weight="bold" /> Add game
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="games-list">
          {games.map((g, i) => {
            const upcoming = g.status !== "completed" && g.date >= now;
            return (
              <motion.button
                key={g.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                onClick={() => navigate(`/games/${g.id}`)}
                data-testid={`game-row-${g.id}`}
                className="w-full text-left flex items-center gap-4 bg-[#121212] border border-zinc-800 p-4 rounded-sm hover:border-[#CCFF00] transition-colors duration-150 group"
              >
                <div className="w-12 h-12 shrink-0 bg-[#0A0A0A] border border-zinc-800 rounded-sm flex items-center justify-center">
                  {g.is_home ? <House size={20} className="text-zinc-400" /> : <Airplane size={20} className="text-zinc-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-label text-[10px] text-zinc-500">{g.is_home ? "HOME" : "AWAY"}</span>
                    {upcoming && <span className="font-label text-[10px] text-[#CCFF00] bg-[#CCFF00]/10 px-1.5 py-0.5 rounded-sm">UPCOMING</span>}
                  </div>
                  <p className="font-heading text-2xl text-white leading-tight truncate">vs {g.opponent}</p>
                  <p className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5">
                    <CalendarBlank size={12} /> {formatDate(g.date)}
                    {g.location && <><MapPin size={12} className="ml-1" /> {g.location}</>}
                  </p>
                </div>
                <div className="text-right">
                  {hasResult(g) ? (
                    <p className={`font-heading text-3xl ${resultColor(g)}`}>{g.score_for}–{g.score_against}</p>
                  ) : g.status === "completed" ? (
                    <p className="font-label text-[10px] text-zinc-600">No result</p>
                  ) : (
                    <p className="font-label text-[10px] text-[#CCFF00]">Scheduled</p>
                  )}
                </div>
                <CaretRight size={20} className="text-zinc-700 group-hover:text-[#CCFF00] transition-colors duration-150" />
              </motion.button>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white" data-testid="game-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Schedule game</DialogTitle>
            <DialogDescription className="text-zinc-500">Add a fixture to your schedule.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Opponent</label>
              <input required value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} data-testid="game-opponent" placeholder="Eastside FC" className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-label text-xs text-zinc-400 block mb-2">Date &amp; time</label>
                <div className="flex gap-2">
                  <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="game-date" className={inputCls} />
                  <select required value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="game-time" className={inputCls}>
                    {TIME_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="font-label text-xs text-zinc-400 block mb-2">Location</label>
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="game-location" placeholder="Home ground" className={inputCls} />
              </div>
            </div>
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Venue</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm({ ...form, is_home: true })} data-testid="game-home" className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm font-label text-xs transition-colors duration-150 ${form.is_home ? "bg-[#CCFF00] text-black" : "bg-[#0A0A0A] border border-zinc-800 text-zinc-400"}`}>
                  <House size={16} /> Home
                </button>
                <button type="button" onClick={() => setForm({ ...form, is_home: false })} data-testid="game-away" className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm font-label text-xs transition-colors duration-150 ${!form.is_home ? "bg-[#CCFF00] text-black" : "bg-[#0A0A0A] border border-zinc-800 text-zinc-400"}`}>
                  <Airplane size={16} /> Away
                </button>
              </div>
            </div>
            {error && <p className="text-[#FF3B30] text-sm" data-testid="game-error">{error}</p>}
            <DialogFooter>
              <button type="button" onClick={() => setDialogOpen(false)} className="font-label text-xs px-4 py-2 text-zinc-400 hover:text-white">Cancel</button>
              <button type="submit" disabled={saving} data-testid="save-game-btn" className="bg-[#CCFF00] text-black font-label text-xs px-5 py-2 rounded-sm hover:bg-[#B3E600] transition-colors duration-150 disabled:opacity-50">
                {saving ? "Saving…" : "Schedule"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
